import { useCallback, useState, type ReactElement } from 'react';
import { Link, useParams } from 'react-router';
import { useChargement, useListePaginee } from '../coquille/useChargement.js';
import { CHEMINS } from '../routes.js';
import {
  suspendreClient, reactiverClient, suspendreProjetClient,
  reactiverProjetClient, archiverProjetClient,
} from '../plateforme.js';

/**
 * LE NIVEAU 2 DU COCKPIT [29-I] : un client, ses projets, et les gestes.
 *
 * ╔═══════════════════════════════════════════════════════════════════════╗
 * ║ AUCUN FILTRE D ETAT. C EST TOUT L INTERET DE CET ECRAN.               ║
 * ╚═══════════════════════════════════════════════════════════════════════╝
 *
 * Les ecrans du monde LOCATAIRE cachent le suspendu et l archive. Ici on voit
 * precisement ce que le client ne voit plus -- sinon on suspendrait sans
 * pouvoir reactiver.
 *
 * ── LES GESTES SONT ICI, PAS DANS L INVENTAIRE ──────────────────────────
 *
 * /plateforme reste en LECTURE SEULE : des boutons destructifs sur chaque
 * carte d une liste de clients invitent au clic de travers. Les gestes de
 * portee CLIENT vivent dans l en-tete de CET ecran ; ceux de portee PROJET
 * sur la carte de LEUR projet.
 *
 * ── L ARCHIVAGE EST TERMINAL, DONC IL SE CONFIRME ───────────────────────
 *
 * Suspendre et reactiver sont reversibles : gestes directs. Archiver ne se
 * defait pas -- aucune route ne desarchive. La confirmation NOMME le projet et
 * distingue « Annuler » de « Archiver definitivement ».
 *
 * L AVERTISSEMENT VIT DANS SON PROPRE <p> [dette 66] : un bouton sur la meme
 * ligne l a tronque en 29-H. Structure, pas style neuf.
 *
 * ── UN COMPROMIS ASSUME : LE NOM DU CLIENT ──────────────────────────────
 *
 * La route des projets ne rend que des projets. Le nom vient donc de
 * l inventaire, filtre ici. Avec six clients c est une requete deja paginee ;
 * au-dela de la premiere page, le client cherche pourrait ne pas s y trouver
 * et l en-tete retomberait sur l identifiant. A revoir si l inventaire grossit.
 */
interface LigneClient {
  readonly id: string; readonly code: string; readonly nom: string;
  readonly etat: string; readonly archive: boolean;
}
interface ReponseClients { readonly clients: readonly LigneClient[] }

interface LigneProjet {
  readonly id: string; readonly code: string; readonly nom: string;
  readonly etat: string; readonly archive: boolean;
  readonly nombreAutomatisations: number;
}
interface ReponseProjets {
  readonly projets: readonly LigneProjet[];
  readonly curseurSuivant: string | null;
}

export function ClientPlateforme(): ReactElement {
  const { idClient } = useParams<{ idClient: string }>();
  const id = idClient ?? '';
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | undefined>(undefined);
  const [aArchiver, setAArchiver] = useState<LigneProjet | undefined>(undefined);

  const inventaire = useChargement<ReponseClients>('/plateforme/clients');
  const extraire = useCallback((r: ReponseProjets) => ({
    lignes: r.projets, curseur: r.curseurSuivant,
  }), []);
  const { etat, chargerSuite, recharger } =
    useListePaginee<ReponseProjets, LigneProjet>(
      `/plateforme/clients/${encodeURIComponent(id)}/projets`, extraire);

  // UN SEUL CHEMIN D ECRITURE : tout geste passe ici, donc l etat « en cours »,
  // le message d echec et le rechargement ne sont ecrits qu UNE fois.
  const agir = useCallback((f: () => Promise<void>) => {
    setEnCours(true);
    setErreur(undefined);
    void f()
      .then(() => { setAArchiver(undefined); recharger(); })
      .catch((e: unknown) => {
        const d = (e as { detail?: { message?: string } }).detail;
        setErreur(d?.message ?? 'Le geste n a pas abouti.');
      })
      .finally(() => { setEnCours(false); });
  }, [recharger]);

  const client = inventaire.etat.phase === 'pret'
    ? inventaire.etat.donnees.clients.find((c) => c.id === id) : undefined;
  const titre = client?.nom ?? 'Client';
  const suspendu = client?.etat === 'suspendu';

  return (
    <div className="contenu">
      {/* ENVELOPPE PROVISOIRE [29-I]. Le monde plateforme n a PAS de coquille
          locataire, donc aucun conteneur ne lui donnait de marge : au smoke du
          07/09 les cartes touchaient le bord et la derniere sortait de l ecran.
          `.contenu` est reprise pour son `padding` -- son `grid-area` est inerte
          hors `.coquille`. Ce n est PAS l enveloppe definitive du monde
          plateforme : l harmonisation de l affichage est 29-M. */}
    <section>
      <p><Link to={CHEMINS.plateforme}>&larr; Inventaire des clients</Link></p>
      <h1>{titre}</h1>
      {client !== undefined && (
        <p className="carte__detail">
          {client.code} — {suspendu ? 'suspendu' : 'actif'}
          {client.archive ? ' — archive' : ''}
        </p>
      )}
      {erreur !== undefined && <p className="etat-vide">{erreur}</p>}

      <h2>Le client</h2>
      <p>
        {suspendu ? (
          <button type="button" disabled={enCours}
            onClick={() => { agir(() => reactiverClient(id)); }}>
            Reactiver ce client
          </button>
        ) : (
          <button type="button" disabled={enCours}
            onClick={() => { agir(() => suspendreClient(id)); }}>
            Suspendre ce client
          </button>
        )}
      </p>

      <h2>Ses projets</h2>
      {etat.phase === 'chargement' && <p className="etat-vide">Chargement…</p>}
      {etat.phase === 'echec' && (
        <p className="etat-vide">Les projets n ont pas pu être chargés.</p>
      )}
      {etat.phase === 'pret' && etat.donnees.lignes.length === 0 && (
        <p className="etat-vide">Ce client n a aucun projet.</p>
      )}
      {etat.phase === 'pret' && etat.donnees.lignes.length > 0 && (
        <>
          {/* Meme motif qu au niveau 1 : `.grille` porte la mise en page,
              les balises sont des BLOCS. */}
          <div className="grille">
            {etat.donnees.lignes.map((p) => (
              <div className="carte" key={p.id}>
                <h2 className="carte__titre">{p.nom}</h2>
                <p className="carte__detail">
                  {p.code} — {p.archive ? 'archive'
                    : p.etat === 'suspendu' ? 'suspendu' : 'actif'}
                  {' — '}{p.nombreAutomatisations} automatisation(s)
                </p>
                  {!p.archive && (
                    <p>
                      {p.etat === 'suspendu' ? (
                        <button type="button" disabled={enCours}
                          onClick={() => { agir(() => reactiverProjetClient(id, p.id)); }}>
                          Reactiver
                        </button>
                      ) : (
                        <button type="button" disabled={enCours}
                          onClick={() => { agir(() => suspendreProjetClient(id, p.id)); }}>
                          Suspendre
                        </button>
                      )}
                      {' '}
                      <button type="button" disabled={enCours}
                        onClick={() => { setAArchiver(p); }}>
                        Archiver…
                      </button>
                    </p>
                  )}
              </div>
            ))}
          </div>
          {etat.donnees.curseurSuivant !== null && (
            <p>
              <button type="button" disabled={etat.donnees.chargeSuite}
                onClick={() => { chargerSuite(); }}>
                {etat.donnees.chargeSuite ? 'Chargement…' : 'Charger la suite'}
              </button>
            </p>
          )}
        </>
      )}

      {aArchiver !== undefined && (
        <div className="carte">
          <h2>Archiver « {aArchiver.nom} » ?</h2>
          {/* L AVERTISSEMENT EST SEUL SUR SA LIGNE [dette 66]. */}
          <p>Cette action est definitive : aucun geste ne desarchive un projet.
            Il disparaitra des ecrans du client et ne pourra plus rien executer.</p>
          <p>
            <button type="button" disabled={enCours}
              onClick={() => { setAArchiver(undefined); }}>Annuler</button>
            {' '}
            <button type="button" disabled={enCours}
              onClick={() => { agir(() => archiverProjetClient(id, aArchiver.id)); }}>
              Archiver definitivement
            </button>
          </p>
        </div>
      )}
    </section>
    </div>
  );
}
